import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { colors, typography } from '../constants';

const UnsavedChangesContext = createContext(null);

/**
 * Asks before unsaved work is left behind.
 *
 * Settings is the one Profit IQ editor that saves on a button rather than as
 * you type, so any move away from it — another tab, another date, another page
 * — can lose edits. It registers what it can do here through
 * `useUnsavedEditor`, and every such move runs through `guard` instead of
 * happening directly.
 */
export function UnsavedChangesProvider({ children }) {
  const editorRef = useRef(null);
  // The move waiting on the answer. Held in state so the dialog renders, and
  // in a ref so the handlers below read the current one.
  const [asking, setAsking] = useState(false);
  const proceedRef = useRef(null);
  const [saving, setSaving] = useState(false);

  // The registered editor keeps its own dirty flag, read through the ref at
  // the moment a question is asked. This mirror exists so a caller that has to
  // *arm* something — the section's router block — can do it only while there
  // is work to lose, which a ref alone would never re-render for.
  const [dirty, setDirty] = useState(false);

  const register = useCallback((editor) => {
    editorRef.current = editor;
  }, []);

  const reportDirty = useCallback((next) => setDirty(next), []);

  const hasUnsaved = useCallback(() => editorRef.current?.isDirty() === true, []);

  const guard = useCallback((proceed) => {
    const editor = editorRef.current;
    if (!editor || !editor.isDirty()) {
      proceed();
      return;
    }
    proceedRef.current = proceed;
    setAsking(true);
  }, []);

  const close = useCallback(() => {
    setAsking(false);
    setSaving(false);
    proceedRef.current = null;
  }, []);

  const take = useCallback(() => {
    const proceed = proceedRef.current;
    close();
    proceed?.();
  }, [close]);

  const onDiscard = useCallback(() => {
    editorRef.current?.discard();
    take();
  }, [take]);

  const onSave = useCallback(async () => {
    setSaving(true);
    // A failed save keeps the edits and stays put, so the retry the Save
    // button offers is still about the same figures.
    const saved = await editorRef.current?.save();
    if (saved) {
      take();
      return;
    }
    close();
  }, [take, close]);

  const value = useMemo(
    () => ({ register, reportDirty, guard, hasUnsaved, dirty }),
    [register, reportDirty, guard, hasUnsaved, dirty]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <Dialog
        open={asking}
        onClose={close}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogContent sx={{ p: 2.75, pb: 1 }}>
          <Typography
            sx={{
              fontSize: typography.subtitle,
              fontWeight: 700,
              color: colors.foreground,
            }}
          >
            Unsaved changes
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: typography.small,
              color: colors.mutedForeground,
              lineHeight: 1.5,
            }}
          >
            Your cost edits have not been saved yet. What would you like to do?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.75, pt: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%',
            }}
          >
            <Button
              variant="contained"
              disabled={saving}
              onClick={onSave}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2.5,
                backgroundColor: colors.primary,
                '&:hover': {
                  backgroundColor: colors.primary,
                  filter: 'brightness(0.95)',
                },
              }}
            >
              {saving ? 'Saving…' : 'Save and continue'}
            </Button>
            <Button
              disabled={saving}
              onClick={onDiscard}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2.5,
                color: colors.destructive,
              }}
            >
              Discard changes
            </Button>
            <Button
              disabled={saving}
              onClick={close}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2.5,
                color: colors.mutedForeground,
              }}
            >
              Keep editing
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
}

function useUnsavedChangesApi() {
  const api = useContext(UnsavedChangesContext);
  if (!api) {
    throw new Error('Profit IQ unsaved-changes guard used outside its section');
  }
  return api;
}

/** Wraps a move that would leave the registered editor. */
export function useUnsavedChangesGuard() {
  return useUnsavedChangesApi().guard;
}

/**
 * Whether anything on screen is holding unsaved work, as a value that
 * re-renders — for callers that have to arm something while it is true.
 */
export function useHasUnsavedWork() {
  return useUnsavedChangesApi().dirty;
}

/**
 * Declares this view as the holder of unsaved work. The callbacks are read at
 * the moment the question is asked, so they never need to be stable.
 */
export function useUnsavedEditor(editor) {
  const { register, reportDirty } = useUnsavedChangesApi();
  const ref = useRef(editor);
  ref.current = editor;

  useEffect(() => {
    register({
      isDirty: () => ref.current.isDirty(),
      save: () => ref.current.save(),
      discard: () => ref.current.discard(),
    });
    // Leaving the view takes its unsaved work with it.
    return () => register(null);
  }, [register]);

  const dirty = editor.isDirty();
  useEffect(() => {
    reportDirty(dirty);
    return () => reportDirty(false);
  }, [dirty, reportDirty]);
}
